import { DialogContent, TextField, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { ElplanInfoTagForDate, PlanValues } from 'api/elplan/elplan.types'
import { isArray, useCurrency } from 'helpers/global.helper/global.helper'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import elplanBidStore from 'store/elplan/elplan'
import { Dialog } from 'ui/components/Dialog/Dialog'
import FormRow from 'ui/components/FormRow/FormRow'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import ModalButtons from 'ui/molecules/ModalButtons/ModalButtons'
import { TimePicker } from 'ui/molecules/pickers/TimePicker/TimePicker'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import {
  FormattedBid,
  FormattedBidWithIdAndTitle,
  generatePlanValues,
} from '../../../DayAheadBidManagement/DayAheadBidManagement.helper'
import TimeEffectTable from '../TimeEffectTable/TimeEffectTable'
import styles from './BidModal.module.less'
import { BidTagSelection } from './BidTagSelection'
import { useBidPriceVolume } from './useBidPriceVolume'
import { useBidTagSelection } from './useBidTagSelection'

type BidModalProps = {
  editBid: (obj: FormattedBidWithIdAndTitle) => void
  createBid?: (obj: FormattedBid) => void
  deleteBid?: (obj: FormattedBidWithIdAndTitle) => void
  bid?: FormattedBidWithIdAndTitle
  closeModal: () => void
  type?: 'spot' | 'block' | 'profile'
  isEditing?: boolean
  tagsForDate?: ElplanInfoTagForDate[]
  requireTagForBlockBids?: boolean
  bidInputCurrency?: string
}

export default function BidModal({
  editBid,
  closeModal,
  bid,
  createBid,
  type,
  deleteBid,
  isEditing,
  tagsForDate = [],
  requireTagForBlockBids = false,
  bidInputCurrency,
}: BidModalProps): ReactElement {
  const tomorrow = Datetime.getTomorrowDate()
  const elplanSnap = useSnapshot(elplanBidStore)
  const systemCurrency = useCurrency()
  const { t } = useTranslation()

  let startHour = ''
  if (bid?.startTime) {
    startHour = bid.startTime
  } else if (elplanSnap.date.start_time) {
    startHour = Datetime.getStartOfDay(elplanSnap.date.start_time)
  } else {
    startHour = tomorrow.startTime
  }

  let endHour = ''
  if (bid?.endTime) {
    endHour = Datetime.ISODatetimeTo59Minutes(bid.endTime as ISODateTime)
  } else if (elplanSnap.date.start_time) {
    endHour = Datetime.ISODatetimeTo59Minutes(Datetime.getEndOfDay(elplanSnap.date.start_time))
  } else {
    endHour = Datetime.ISODatetimeTo59Minutes(tomorrow.endTime)
  }

  const [hoursPeriod, setHoursPeriod] = useState<{ startHour: ISODateTime; endHour: ISODateTime }>({
    startHour: startHour as ISODateTime,
    endHour: endHour as ISODateTime,
  })

  const initialVolumeValue = isArray(bid?.volume) ? (bid?.volume?.[0] ?? 0) : (bid?.volume ?? 0)
  const [planValues, setPlanValues] = useState<PlanValues[]>(bid?.planValues ?? [])

  const { price, volume } = useBidPriceVolume({
    type,
    initialPrice: bid?.price ?? 0,
    initialVolume: initialVolumeValue,
  })

  const bidType = type === 'block' ? t('Block bid') : type === 'spot' ? t('Spot bid') : t('Profile bid')
  const modalTitle = isEditing ? `${t('Edit')} ${bid?.title ?? bidType}` : `${t('Create')} ${bidType}`

  const hourLimit =
    type === 'block' || type === 'profile'
      ? elplanSnap.blockBidInfo.minimum_hours_for_bid
      : elplanSnap.spotBidWithoutPriceInfo.minimum_hours_for_bid
  // getHoursBetween normalizes a reversed range instead of measuring it as empty, so a reversed
  // period must be rejected here rather than relied on to fail the minimum-hours check below.
  const isPeriodReversed = Datetime.isBeforeOrEqual(hoursPeriod.endHour, hoursPeriod.startHour)
  const hourPeriodIsLessThanMinimum = hourLimit
    ? Datetime.getHoursBetween(hoursPeriod.startHour, hoursPeriod.endHour).length <= hourLimit - 1
    : false
  const hourLimitValidationErrorText =
    hourLimit === 1 ? hourLimit + ` ` + t('hour') + `.` : hourLimit + ` ` + t('hours') + `.`
  const periodValidationMessage = isPeriodReversed
    ? t('End time must be after start time')
    : hourPeriodIsLessThanMinimum
      ? `${t('Minimum hour length of bid has to be at least')} ${hourLimitValidationErrorText}`
      : undefined
  const canSelectTags = type === 'block' || type === 'profile'
  const tagSelection = useBidTagSelection({
    tagsForDate,
    requireTagForBlockBids,
    canSelectTags,
    initialTagId: bid?.tag ?? null,
  })
  const currency = bidInputCurrency ?? systemCurrency

  function updatePlanValues(period: { startHour: ISODateTime; endHour: ISODateTime }): void {
    if (type === 'profile') {
      const oldPlanValues = planValues ?? []
      const newPlanValues = generatePlanValues(0, period.startHour, new Datetime(period.endHour).addHours(-1).ISOString)

      const updatedValues = newPlanValues.map((value) => {
        const endTimeHasMinutes59 = new Date(value.end_time).getMinutes() === 59
        const formattedEndTime = new Date(value.end_time)
        if (endTimeHasMinutes59) {
          //Minutes for newPlanValues.endTime is HH:59 and oldPlanValues.endTime is HH+1:00
          formattedEndTime.setMinutes(formattedEndTime.getMinutes() + 1)
        }
        const existingValue = oldPlanValues.find(
          (v) =>
            v.start_time === value.start_time &&
            Datetime.isSameHour(v.end_time, formattedEndTime.toISOString() as ISODateTime) &&
            v.volume !== 0
        )
        value = existingValue ? existingValue : value
        return value
      })
      setPlanValues(updatedValues)
    }
  }

  function generatePlanValuesFromBid(bid: FormattedBidWithIdAndTitle): PlanValues[] {
    if (!isArray(bid?.volume)) {
      return [
        {
          volume: bid.volume as number,
          start_time: new Datetime(bid.startTime).ISOString,
          end_time: new Datetime(bid.endTime).ISOString,
        },
      ]
    }

    const planValues =
      bid?.volume?.map((value, index) => {
        const start_time = new Datetime(bid.startTime).addHours(index).ISOString
        const end_time = new Datetime(bid.startTime).addHours(index + 1).ISOString
        return { start_time, end_time, volume: value }
      }) ?? []
    return planValues
  }

  if (type === 'profile' && bid?.volume && !planValues.length) {
    setPlanValues(generatePlanValuesFromBid(bid))
  }

  return (
    <Dialog open={true} aria-labelledby="form-dialog-title" maxWidth="md" fullWidth>
      <CustomDialogTitle title={modalTitle} handleClose={() => closeModal()} />
      <DialogContent>
        <div className={styles.BidModal_Date}>
          <Typography>
            <strong>{`${t('Date')}:`}</strong>{' '}
            {`${Datetime.toLocalTime(hoursPeriod.startHour, 'shortDayText')} ${Datetime.toLocalTime(
              hoursPeriod.startHour,
              'onlyDate'
            )}`}
          </Typography>
        </div>
        <div className={styles.BidModal_TimePickers}>
          <FormRow>
            <Grid size={{ xs: 12, md: 12, lg: 6 }}>
              <TimePicker
                error={isPeriodReversed || hourPeriodIsLessThanMinimum}
                errorMessage={periodValidationMessage}
                value={hoursPeriod.startHour}
                label={t('From')}
                onChange={(newStartHour) => {
                  const newPeriod = { startHour: newStartHour, endHour: hoursPeriod.endHour }
                  setHoursPeriod(newPeriod)
                  updatePlanValues(newPeriod)
                }}
                dateTransform={Datetime.ISODatetimeTo00Minutes}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12, lg: 6 }}>
              <TimePicker
                error={isPeriodReversed || hourPeriodIsLessThanMinimum}
                errorMessage={periodValidationMessage}
                label={t('To')}
                value={hoursPeriod.endHour}
                onChange={(newEndHour) => {
                  const newPeriod = { startHour: hoursPeriod.startHour, endHour: newEndHour }
                  setHoursPeriod(newPeriod)
                  updatePlanValues(newPeriod)
                }}
                dateTransform={Datetime.ISODatetimeTo59Minutes}
              />
            </Grid>
            {periodValidationMessage && (
              <Grid size={{ xs: 12 }}>
                <Typography color="error">{periodValidationMessage}</Typography>
              </Grid>
            )}
          </FormRow>
        </div>
        <div className={styles.BidModal_TextFields}>
          {(type === 'block' || type === 'spot') && (
            <FormRow>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <TextField
                  id={'volume'}
                  label={t('Volume') + ' (MW)'}
                  aria-label={'volume'}
                  disabled={false}
                  variant="outlined"
                  fullWidth
                  value={volume.value}
                  error={Boolean(volume.error)}
                  helperText={volume.error}
                  slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  onChange={(e) => {
                    volume.onChange(e.target.value)
                  }}
                />
              </Grid>
            </FormRow>
          )}
        </div>
        {(type === 'block' || type === 'profile') && (
          <div className={styles.BidModal_TextFields}>
            <FormRow>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <TextField
                  id={'electricityPrice'}
                  label={t('Avg price') + ` (${currency}/MWh)`}
                  aria-label={'electricityPrice'}
                  disabled={false}
                  variant="outlined"
                  fullWidth
                  value={price.value}
                  error={Boolean(price.error)}
                  helperText={price.error}
                  slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  onChange={(e) => {
                    price.onChange(e.target.value)
                  }}
                />
              </Grid>
            </FormRow>
          </div>
        )}
        {(type === 'block' || type === 'profile') && requireTagForBlockBids && (
          <div className={styles.BidModal_TextFields}>
            <BidTagSelection
              productionConsumption={tagSelection.effectiveProductionConsumption}
              selectedTagId={tagSelection.selectedTagId}
              groupedTagOptions={tagSelection.groupedTagOptions}
              hasAvailableTagsForSelectedType={tagSelection.hasAvailableTagsForSelectedType}
              tagValidationError={tagSelection.tagValidationError}
              onProductionConsumptionChange={tagSelection.handleProductionConsumptionChange}
              onTagChange={tagSelection.handleTagChange}
            />
          </div>
        )}
        {type === 'profile' && (
          <FormRow>
            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
              <TimeEffectTable
                currentPlanValues={planValues}
                onCellChange={(value, index) => {
                  planValues[index].volume = value
                }}
              />
            </Grid>
          </FormRow>
        )}
        <div className={styles.BidModal_TextFields}>
          <FormRow>
            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
              <TextField
                id={'name'}
                label={t('Name (optional)')}
                aria-label={'name'}
                variant="outlined"
                fullWidth
                defaultValue={bid?.name}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement
                  if (bid && target.value) {
                    bid.name = target.value
                  }
                }}
              />
            </Grid>
          </FormRow>
        </div>
        <ModalButtons
          onSubmit={() => {
            if (!price.validate()) {
              return
            }
            if (!volume.validate()) {
              return
            }
            if (!tagSelection.validateTagBeforeSubmit()) {
              return
            }

            if (type === 'block') {
              const obj = {
                type: bid?.type ?? 'blockbid',
                tag: tagSelection.tagToSubmit,
                fake: bid?.fake,
                block_order: bid?.block_order,
                displayStartTime: hoursPeriod.startHour,
                displayEndTime: hoursPeriod.endHour,
                startTime: Datetime.toISOString(hoursPeriod.startHour),
                endTime: Datetime.ISODatetimeTo00Minutes(hoursPeriod.endHour),
                volume: [volume.getNumeric()],
                price: price.getNumeric(),
                editable: true,
                clear: true,
                name: bid?.name,
                scenarioId: bid?.scenarioId,
                partitionId: bid?.partitionId,
              }
              if (bid) {
                editBid({ ...obj, id: bid.id, title: bid.title })
              } else if (createBid) {
                createBid(obj)
              }
            }

            if (type === 'spot') {
              const obj: FormattedBid = {
                type: bid?.type ?? 'spotbid_without_price',
                displayStartTime: hoursPeriod.startHour,
                displayEndTime: hoursPeriod.endHour,
                startTime: Datetime.toISOString(hoursPeriod.startHour),
                endTime: Datetime.ISODatetimeTo00Minutes(hoursPeriod.endHour),
                volume: volume.getNumeric(),
                editable: true,
                clear: true,
                scenarioId: bid?.scenarioId,
              }
              if (bid) {
                editBid({ ...obj, id: bid.id, title: bid.title })
              } else if (createBid) {
                createBid(obj)
              }
            }

            if (type === 'profile') {
              const obj: FormattedBid = {
                type: 'profilebid',
                tag: tagSelection.tagToSubmit,
                fake: bid?.fake,
                block_order: bid?.block_order,
                displayStartTime: hoursPeriod.startHour,
                displayEndTime: hoursPeriod.endHour,
                startTime: Datetime.toISOString(hoursPeriod.startHour),
                endTime: Datetime.ISODatetimeTo00Minutes(hoursPeriod.endHour),
                volume: planValues.map((value) => value.volume),
                price: price.getNumeric(),
                editable: true,
                clear: true,
                name: bid?.name,
                scenarioId: bid?.scenarioId,
                partitionId: bid?.partitionId,
              }
              if (bid) {
                editBid({ ...obj, id: bid.id, title: bid.title })
              } else if (createBid) {
                createBid(obj)
              }
            }

            closeModal()
          }}
          onCancel={closeModal}
          onDelete={
            isEditing && deleteBid
              ? () => {
                  closeModal()
                }
              : void 0
          }
          disableSubmit={isPeriodReversed || hourPeriodIsLessThanMinimum}
          infoText={
            elplanSnap.elplan.has_plan
              ? t(
                  'There is already an electrity plan created for this period. Your changes will not affect the current electricity plan. Would you still like to make changes?'
                )
              : undefined
          }
        />
      </DialogContent>
    </Dialog>
  )
}
